import { ApiService, GetListResponse, ModelBase } from './api.service';
import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SoftwareVersionsObject } from '@app/api/software-images-api.service';
import { RequiredSoftwareNotificationComponent } from '@app/pages/acs/dialogs/required-software-notification.component';
import { NotificationService } from '@agilent/ui-element';

export interface AcCredentials {
    aic: {
        user: string;
        password: string;
    };
    cockpit: {
        user: string;
        password: string;
    };
}

export interface NetworkInterfaceCard {
    name: string;
    mac_address: string;
    is_connected: boolean;
    reported_ip_address: string;
    is_default: boolean;
    ip_address: {
        selected: 'automatic' | 'manual';
        options: {
            automatic: null;
            manual: {
                ip_address: string;
                subnet_mask: string;
                default_gateway: string;
            };
        };
    };
    dns: {
        selected: 'automatic' | 'manual';
        options: {
            automatic: null;
            manual: {
                preferred_dns: string;
                alternate_dns: string;
            }
        }
    };
}

export interface AC extends ModelBase {
    certificate_arn: string;
    customer_id: number;
    name: string;
    thing_name: string;
    shadow_json?: string;
    ac_dashboard_name: string;
    mac_address: string;
    register_ip_address: string;
    register_fqdn: string;
    register_token_name: string;
    identity: string;
    online_status?: string;
    last_connection_timestamp?: string;
    platform: string;
    location: string;
    description: string;
    network_cards_json?: string;
    openlab_system_id: number;
    fqdn: string;
    is_locked: boolean;
    is_inheriting_software_from_ols?: boolean;
    software_versions_json?: SoftwareVersionsObject;
    ols_software_versions_json?: SoftwareVersionsObject;
    ols_name?: string;
    last_ok_sw_update?: string;
    cid_status?: string;
    pin_code: string;
    update_status: string;
    api_heartbeat_timestamp?: string;
}

export function acNameValidator(control: FormControl): { [s: string]: boolean } {
    // The AC name is valid if it contains five or more alphanumeric
    // and dash characters (a-z, A-Z, 0-9, -), and has a length
    // of 64 characters or less.
    const name = control.value;
    const reducedName = name.replace(/[^A-Za-z0-9-]/g, '');
    if (reducedName.length < 5 || name.length > 64) {
        return {'invalid': true};
    }
}

export interface TunnelSession {
    thing_name: string;
    tunnel_created_at: string;
    cockpit_url: string;
    aic_admin_password: string;
}

interface GetACsArguments {
    customerId: number;
    offset?: number;
    limit?: number;
    shadow_command?: string; // if provided, will return data including latest shadow history for command
}

interface RunCommandArguments {
    ac_ids: number [];
    commandName: string;

    [propName: string]: any; // optional parameters
}

export interface RunCommandsArguments {
    name: string;
    input_params: {};
    reason?: string;
}

interface GetACsResponse extends GetListResponse {
    acs: AC[];
}

export enum ACUpdateStatus {
    UPDATING = 'updating',
    DOWNLOADING = 'downloading',
    CACHING = 'caching',
    READY = 'ready',
    NO_UPDATE_REQUIRED = 'no_update_required',
    REGISTERING = 'registering'
}

export enum AcInstrumentStatus {
    PAUSED = 'paused',
    WAITING_FOR_USER = 'waiting_for_user',
    ACQUIRING = 'acquiring',
    IN_REVIEW = 'in_review',
    OK = 'ok',
    DISCONNECTED = 'disconnected',
    UNKNOWN = 'unknown'
}

export enum AcCdsStatus {
    NOT_INSTALLED = 'Not Installed',
    RUNNING = 'Running',
    UPDATING = 'Updating',
}

export enum CidStatus {
    DISCONNECTED = 'disconnected',
    CDS_NOT_RUNNING = 'cds_not_running',
    SERVER_DISCONNECTED = 'server_disconnected',
    READY = 'ready',
    IN_USE = 'in_use',
    NEW = 'new'
}

export enum ACCommand {
    GET_INSTRUMENTS_INFO = 'get_instruments_info',
    INSTALL_AC_IMAGE = 'install_ac_image',
    INSTALL_CDS_DRIVER = 'install_cds_driver',
    INSTALL_WINDOWS_UPDATE = 'install_windows_update',
    REBOOT_AC = 'reboot_ac',
    REBOOT_WINDOWS_VM = 'reboot_windows_vm',
    RESTART_AC_AGENT = 'restart_agent',
    SET_AC_HOSTNAME = 'set_ac_hostname',
    SET_AIC_CONFIG = 'set_aic_config',
    SET_AIC_NETWORK = 'set_aic_network',
    SET_NIC_CONFIG = 'set_nic_config',
    UPDATE_AC_CREDENTIALS = 'update_credentials',
    SCHEDULE_OR_RUN_AC_ANTIVIRUS_SCAN = "schedule_or_run_ac_antivirus_scan",
    UPDATE_AGENT = 'update_agent',
    UPDATE_AIC_CREDENTIALS = 'update_aic_credentials',
    UPDATE_WINDOWS_VM_IMAGE = 'update_windows_vm_image'
}

export interface IotShadow {
    ac_id: number;
    created_date: string;
    customer_id: number;
    shadow_json: any;
    version: number;
}

export interface ACLog {
    ac_id: number;
    created_date: string;
    log_type: string;
    log_details: {};
}

interface GetIotShadowsArguments {
    acId: number;
    offset: number;
    limit: number;
    include_commands?: boolean;
}

interface GetIotShadowsResponse extends GetListResponse {
    ac_iot_shadows: IotShadow[];
    command_shadows: { [command_name: string]: IotShadow };
}
interface GetACLogsResponse extends GetListResponse {
    ac_logs: ACLog[];
    command_shadows: { [command_name: string]: IotShadow };
}

export interface WindowsUpdate {
    version: string,
    status: string,
    kbArticleIds: string,
    releaseDate: string,
    notes: string
}

export interface WindowsUpdateImportAction {
    action: string,
    changes: [string],
    update: WindowsUpdate
}

export const acNamePattern: RegExp = /^[a-zA-Z0-9-]+$/;
export const acNamePatternNotOnlyNumbers: RegExp = /^(?![0-9]+$)[\s\S]*$/;
export const acNameLengthPattern: RegExp = /^.{3,15}$/;

@Injectable({
    providedIn: 'root'
})
export class ACApiService {
    constructor(
        private apiService: ApiService,
        private notificationService: NotificationService
    ) {
    }

    /**
     * Returns a list of all ACs for a given customer
     * @param args The default argument list required to return a list of ACs
     * for the command in the returned AC data
     */
    public async getACs(args: GetACsArguments): Promise<GetACsResponse> {
        const {customerId, offset, limit, shadow_command} = args;
        return this.apiService.getResponseJson({
            urlPart: '/acs',
            queryStringParams: {
                customer_id: customerId,
                include_shadow_for: shadow_command,
                offset: offset,
                limit: limit
            }
        });
    }

    public async getCidListData(args: GetACsArguments): Promise<GetACsResponse> {
        const {customerId} = args;
        return this.apiService.getResponseJson({
            urlPart: '/cids',
            queryStringParams: {
                customer_id: customerId
            }
        });
    }

    public async getAC(
        args: {
            acId: number,
            shadow_command?: string,
            include_online_status?: boolean
        }
    ): Promise<{ ac: AC }> {
        const {acId, shadow_command, include_online_status} = args;
        return this.apiService.getResponseJson({
            urlPart: `/acs/${acId}`,
            queryStringParams: {
                include_shadow_for: shadow_command,
                include_online_status: include_online_status
            }
        });
    }

    public async deleteTunnelServerCookie(thingName: string): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/acs/${thingName}/tunnel_session/delete_cookie`,
        });
    }

    public async getInstrumentWarning(args: {ac_ids: number[]}): Promise<{ return_instrument_warning: boolean }>
    {
        return this.apiService.getResponseJson({
            urlPart: '/acs/instrument_warning',
            queryStringParams: {
                ac_ids: args.ac_ids
            }
        });
    }

    public async applyAllUpdates(args: {ac_ids: number[], skipCidsInUse: boolean}): Promise<any> {
        const data = {
            ac_ids: args.ac_ids,
            skip_cids_in_use: args.skipCidsInUse
        };
        return this.apiService.getResponseJson({
            urlPart: `/acs/apply_all_updates`,
            method: 'POST',
            data
        });
    }

    public async allowChangesToAcList(args: {ac_ids: number[], reason: string}): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: '/acs/allow_changes_list',
            method: 'POST',
            data: {
                ac_ids: args.ac_ids,
                reason: args.reason
            }
        });
    }

    public async disallowChangesToAcList(args: {ac_ids: number[]}): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: '/acs/disallow_changes_list',
            method: 'POST',
            data: {
                ac_ids: args.ac_ids
            }
        });
    }

    public async createCid(args: {
        customerId: number,
        name: string,
        fqdn: string,
        openLabSystemId: number,
        location: string,
        description: string,
        pinCode: string
    }): Promise<{
        pin_code: string,
        ac: AC,
        reg_info: any
    }> {
        const {
            customerId, name, fqdn, openLabSystemId, location, description, pinCode
        } = args;

        return this.apiService.getResponseJson({
            urlPart: '/cids/new',
            method: 'POST',
            data: {
                customer_id: customerId,
                name: name,
                fqdn: fqdn,
                openlab_system_id: openLabSystemId,
                location: location,
                description: description,
                pin_code: pinCode
            }
        });
    }

    public async updateAC(args: { ac: AC }): Promise<{ ac: AC }> {
        const {ac} = args;
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac.id}`,
            method: 'PUT',
            data: ac
        });
    }

    public async deleteCid(
        args: { ac: AC, reason: string },
    ): Promise<{ ac: AC }> {
        const {ac, reason} = args;
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac.id}`,
            method: 'DELETE',
            data: {
                reason: reason
            }
        });
    }

    public async getACLogs(
        args: GetIotShadowsArguments
    ): Promise<GetACLogsResponse> {
        const {acId, offset, limit, include_commands} = args;
        return this.apiService.getResponseJson({
            urlPart: `/acs/${acId}/logs`,
            queryStringParams: {
                offset: offset,
                limit: limit,
                include_commands: include_commands
            }
        });
    }

    public async lockAC(args: { ac: AC, reason?: string }): Promise<{ ac: AC }> {
        const {ac, reason} = args;
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac.id}/lock`,
            method: 'POST',
            data: {
                user_reason: reason
            }
        });
    }

    public async deleteCommand(
        args: { command_name: string, ac_id: number },
    ): Promise<any> {
        const {command_name, ac_id} = args;
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac_id}/command/${command_name}`,
            method: 'DELETE'
        });
    }

    public async getCIDsInformationReport(args: {cid_ids: number[]}): Promise<{ "result": {} }>
    {
        return this.apiService.getResponseJson({
            urlPart: '/cids/report/information',
            queryStringParams: {
                cid_ids: args.cid_ids
            }
        });
    }

    /**
     * Tunnel Sessions
     */

    public async getTunnelSession(ac_id): Promise<{ session: TunnelSession }> {
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac_id}/tunnel_session`,
            method: 'GET'
        });
    }

    public async createTunnelSession(ac_id: number, dest: string, reason: string): Promise<{ session: TunnelSession }> {
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac_id}/tunnel_session/new`,
            method: 'POST',
            data: {
                destination: dest,
                reason
            }
        });
    }

    public async deleteTunnelSession(ac_id: number) {
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac_id}/tunnel_session`,
            method: 'DELETE'
        });
    }


    public async getRemoteAccessCredentials(ac_id: number, destinations: string[]): Promise<AcCredentials> {
        return this.apiService.getResponseJson({
            urlPart: `/acs/${ac_id}/tunnel_session/credentials`,
            method: 'POST',
            data: {
                destinations: destinations
            }
        });
    }

    public async getACStatuses(thingNames: string[]): Promise<{}> {
        return this.apiService.getResponseJson({
            urlPart: '/acs/status',
            method: 'POST',
            data: thingNames
        });
    }

    public async runAcCommands(args: {
        ac_ids: number[],
        commands: RunCommandsArguments[],
        reason?: string }): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/acs/commands`,
            method: 'POST',
            data: args
        });
    }

    executeACCommand = async (ac: AC, acCommand: string, args = {}, reason?: string) => {
        try {
             const result = await this.runAcCommands(
                {
                    ac_ids: [ac.id],
                    commands: [
                        {
                            name: acCommand,
                            input_params: args
                        }
                    ],
                    reason
                }
            );
            if (result.errors.length > 0) {
                const errorMessage = result.errors[0].message;

                if (errorMessage['minimum_required_software_versions']) {
                    this.notificationService.showModal(
                        RequiredSoftwareNotificationComponent,
                        { requirements: errorMessage['minimum_required_software_versions'] });
                } else {
                    this.notificationService.error(result.errors[0].message);
                }
            } else {
                this.notificationService.success('Command initiated');
            }
        } catch (ex) {
            console.error('ERROR: ', ex);
        }
    }

    public async resetOpenLabCDS(acId: number, reason: string): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/acs/${acId}/reset-openlab`,
            method: 'POST',
            data: {
                reason
            }
        });
    }

    public async postActivityLogGridQuery(gridRequest, acId): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/acs/${acId}/activity-log/grid-query`,
            method: 'POST',
            data: gridRequest
        });
      }

    async getAcLogData(offset: number, acId, limit: number): Promise<any> {
        try {
            return await this.getACLogs({
                acId: acId,
                offset: offset,
                limit: limit,
                include_commands: true
            });
        } catch (ex) {
            console.log('error: ', ex);
        }
    }
    async getAllWindowsUpdates(): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/windows-updates/all`,
            method: 'GET'
        });
    }
    async fetchWindowsUpdateImportActions(args: {
        records: WindowsUpdate[],
        version: string}): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/windows-updates/import-actions`,
            method: 'POST',
            data: args
        });
    }

    async importWindowsUpdates(args: {
        windowsUpdates: WindowsUpdate[],
        reason: string}): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/windows-updates/import`,
            method: 'POST',
            data: args
        });
    }

    async deleteWindowsUpdate(args: {
        reason: string}, windows_update_id: number): Promise<any> {
        return this.apiService.getResponseJson({
            urlPart: `/windows-updates/${windows_update_id}`,
            method: 'DELETE',
            data: args
        });
    }
}
